import { create } from 'zustand';

/**
 * Personal accessibility and comfort settings (spec §14). They belong to this browser, not the
 * account: a big monitor at the office and a laptop at home want different UI scales.
 */

/** Game actions whose key can be changed. The arrow keys always move as well. */
export const BINDABLE_ACTIONS = [
  'up',
  'down',
  'left',
  'right',
  'interact',
  'sprint',
  'emote',
] as const;
export type BindableAction = (typeof BINDABLE_ACTIONS)[number];

/** A key as Phaser knows it (`keyCode`) plus what to show for it. */
export interface KeyBinding {
  readonly keyCode: number;
  readonly label: string;
}

export type KeyBindings = Record<BindableAction, KeyBinding>;
export type ReduceMotion = 'system' | 'on' | 'off';
export type ChatFontSize = 'small' | 'medium' | 'large';

export interface Settings {
  /** Multiplies the size of all React UI (0.8–1.5). */
  readonly uiScale: number;
  readonly chatFontSize: ChatFontSize;
  /** How dark night gets, 0–100 % of the real sun (spec §6.10). */
  readonly darkness: number;
  /** `system` follows the operating system's "reduce motion". */
  readonly reduceMotion: ReduceMotion;
  readonly keys: KeyBindings;
}

export const UI_SCALE_RANGE = { min: 0.8, max: 1.5, step: 0.1 } as const;
export const CHAT_FONT_SCALE: Record<ChatFontSize, number> = {
  small: 0.85,
  medium: 1,
  large: 1.25,
};
/** Root font size in `styles.css`; the UI scale multiplies it. */
const BASE_FONT_PX = 15;

export const DEFAULT_KEYS: KeyBindings = {
  up: { keyCode: 87, label: 'W' },
  down: { keyCode: 83, label: 'S' },
  left: { keyCode: 65, label: 'A' },
  right: { keyCode: 68, label: 'D' },
  interact: { keyCode: 69, label: 'E' },
  sprint: { keyCode: 32, label: 'Space' },
  emote: { keyCode: 90, label: 'Z' },
};

export const DEFAULT_SETTINGS: Settings = {
  uiScale: 1,
  chatFontSize: 'medium',
  darkness: 100,
  reduceMotion: 'system',
  keys: DEFAULT_KEYS,
};

/** Keys that already mean something everywhere (chat, closing dialogs, focus). */
const RESERVED_KEY_CODES = new Set([9, 13, 27, 37, 38, 39, 40]); // Tab, Enter, Esc, arrows

const STORAGE_KEY = 'veriverse.settings';

const clamp = (value: unknown, min: number, max: number, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.min(max, Math.max(min, value))
    : fallback;

const oneOf = <T extends string>(value: unknown, options: readonly T[], fallback: T): T =>
  typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback;

function parseBinding(value: unknown): KeyBinding | undefined {
  if (typeof value !== 'object' || value === null) return undefined;
  const { keyCode, label } = value as Partial<KeyBinding>;
  if (typeof keyCode !== 'number' || !Number.isInteger(keyCode) || RESERVED_KEY_CODES.has(keyCode))
    return undefined;
  if (typeof label !== 'string' || label.length === 0 || label.length > 12) return undefined;
  return { keyCode, label };
}

/** Bindings from storage; any broken or duplicated entry falls back to the defaults. */
function parseKeys(value: unknown): KeyBindings {
  const raw = typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};
  const keys = { ...DEFAULT_KEYS };
  for (const action of BINDABLE_ACTIONS)
    keys[action] = parseBinding(raw[action]) ?? DEFAULT_KEYS[action];
  const codes = BINDABLE_ACTIONS.map((action) => keys[action].keyCode);
  return new Set(codes).size === codes.length ? keys : DEFAULT_KEYS;
}

/** Reads stored settings defensively: anything missing or out of range gets its default. */
export function parseSettings(stored: unknown): Settings {
  const raw =
    typeof stored === 'object' && stored !== null ? (stored as Record<string, unknown>) : {};
  const d = DEFAULT_SETTINGS;
  return {
    uiScale: clamp(raw.uiScale, UI_SCALE_RANGE.min, UI_SCALE_RANGE.max, d.uiScale),
    chatFontSize: oneOf(raw.chatFontSize, ['small', 'medium', 'large'] as const, d.chatFontSize),
    darkness: clamp(raw.darkness, 0, 100, d.darkness),
    reduceMotion: oneOf(raw.reduceMotion, ['system', 'on', 'off'] as const, d.reduceMotion),
    keys: parseKeys(raw.keys),
  };
}

/**
 * What a key press would be bound as, or `undefined` for keys that cannot be bound (reserved
 * keys, modifiers, dead keys). `code` is `KeyboardEvent.keyCode`, which Phaser uses.
 */
export function bindingFromKey(key: string, code: number): KeyBinding | undefined {
  if (RESERVED_KEY_CODES.has(code) || code === 0) return undefined;
  if (['Shift', 'Control', 'Alt', 'Meta', 'Dead', 'CapsLock', 'Unidentified'].includes(key))
    return undefined;
  const label = key === ' ' ? 'Space' : key.length === 1 ? key.toUpperCase() : key;
  return label.length > 12 ? undefined : { keyCode: code, label };
}

/**
 * Binds `action` to `binding`. If another action already uses that key, the two swap, so every
 * action keeps a key.
 */
export function rebind(
  keys: KeyBindings,
  action: BindableAction,
  binding: KeyBinding,
): KeyBindings {
  const next = { ...keys };
  const clash = BINDABLE_ACTIONS.find(
    (other) => other !== action && keys[other].keyCode === binding.keyCode,
  );
  if (clash) next[clash] = keys[action];
  next[action] = binding;
  return next;
}

/** Whether reduced motion is in effect: the setting, or the OS preference when set to `system`. */
export function motionReduced(setting: ReduceMotion, systemPrefers: boolean): boolean {
  return setting === 'system' ? systemPrefers : setting === 'on';
}

function loadSettings(): Settings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return parseSettings(stored ? JSON.parse(stored) : undefined);
  } catch {
    return DEFAULT_SETTINGS; // storage blocked or corrupt
  }
}

interface SettingsState extends Settings {
  update(patch: Partial<Settings>): void;
  reset(): void;
}

export const useSettingsStore = create<SettingsState>()((set, get) => ({
  ...loadSettings(),
  update: (patch) => {
    set(parseSettings({ ...pick(get()), ...patch }));
    persist(pick(get()));
  },
  reset: () => {
    set(DEFAULT_SETTINGS);
    persist(DEFAULT_SETTINGS);
  },
}));

function pick(state: Settings): Settings {
  const { uiScale, chatFontSize, darkness, reduceMotion, keys } = state;
  return { uiScale, chatFontSize, darkness, reduceMotion, keys };
}

function persist(settings: Settings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* non-essential preference */
  }
}

/** Whether the world should hold still right now (the setting, or the OS when `system`). */
export function prefersReducedMotion(): boolean {
  const system =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  return motionReduced(useSettingsStore.getState().reduceMotion, system);
}

/** Applies the UI scale and chat font size to the page, now and whenever they change. */
export function applyUiSettings(): () => void {
  const apply = ({ uiScale, chatFontSize }: Settings) => {
    const root = document.documentElement;
    root.style.fontSize = `${BASE_FONT_PX * uiScale}px`;
    root.style.setProperty('--chat-font-scale', String(CHAT_FONT_SCALE[chatFontSize]));
  };
  apply(useSettingsStore.getState());
  return useSettingsStore.subscribe(apply);
}
